import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, Play, Youtube } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { videosAPI } from '../../utils/api';

const categories = ['Tourism', 'Outdoors', 'Lifestyle', 'Business', 'Community', 'Events'];

export const VideoManager = () => {
  const { toast } = useToast();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    youtube_id: '',
    category: 'Tourism',
    duration: '',
    order: 0,
    active: true
  });

  const fetchVideos = useCallback(async () => {
    try {
      const response = await videosAPI.getAllAdmin();
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast({
        title: "Error",
        description: "Failed to load videos.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Extract YouTube ID from URL
  const extractYoutubeId = (input) => {
    // If it's already just an ID (11 characters, alphanumeric)
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
      return input;
    }
    // Try to extract from various URL formats
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = input.match(regex);
    return match ? match[1] : input;
  };

  const handleAdd = async () => {
    if (!newVideo.title || !newVideo.youtube_id) {
      toast({ title: "Error", description: "Title and YouTube ID/URL are required.", variant: "destructive" });
      return;
    }
    try {
      const dataToSend = {
        ...newVideo,
        youtube_id: extractYoutubeId(newVideo.youtube_id)
      };
      await videosAPI.create(dataToSend);
      toast({ title: "Video Added", description: "Video has been added to the gallery." });
      setIsAdding(false);
      setNewVideo({
        title: '', description: '', youtube_id: '', category: 'Tourism', duration: '', order: 0, active: true
      });
      fetchVideos();
    } catch (error) {
      console.error('Error creating video:', error);
      toast({ title: "Error", description: "Failed to add video.", variant: "destructive" });
    }
  };

  const handleEdit = (video) => {
    setEditingId(video.id);
    setEditForm({ ...video });
  };

  const handleSaveEdit = async () => {
    try {
      const dataToSend = {
        ...editForm,
        youtube_id: extractYoutubeId(editForm.youtube_id)
      };
      await videosAPI.update(editingId, dataToSend);
      toast({ title: "Video Updated", description: "Video has been updated." });
      setEditingId(null);
      setEditForm({});
      fetchVideos();
    } catch (error) {
      console.error('Error updating video:', error);
      toast({ title: "Error", description: "Failed to update video.", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await videosAPI.delete(id);
        toast({ title: "Video Deleted", description: "Video has been removed." });
        fetchVideos();
      } catch (error) {
        console.error('Error deleting video:', error);
        toast({ title: "Error", description: "Failed to delete video.", variant: "destructive" });
      }
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading videos...</div>;
  }

  const renderForm = (data, setData, isEdit = false) => (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Video Title *</label>
          <input
            type="text"
            value={data.title}
            onChange={(e) => setData({ ...data, title: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-red-500"
            placeholder="e.g., Cornwall Waterfront Tour"
            data-testid="video-title-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
          <select
            value={data.category}
            onChange={(e) => setData({ ...data, category: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-red-500"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">YouTube URL or Video ID *</label>
        <input
          type="text"
          value={data.youtube_id}
          onChange={(e) => setData({ ...data, youtube_id: e.target.value })}
          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-red-500"
          placeholder="https://www.youtube.com/watch?v=... or just the video ID"
          data-testid="video-youtube-input"
        />
        <p className="text-xs text-slate-500 mt-1">Paste a full YouTube URL or just the video ID (e.g., dQw4w9WgXcQ)</p>
      </div>

      {/* Preview */}
      {data.youtube_id && extractYoutubeId(data.youtube_id).length === 11 && (
        <div className="bg-slate-50 p-4 rounded-lg">
          <p className="text-sm font-medium text-slate-700 mb-2">Preview:</p>
          <img 
            src={`https://img.youtube.com/vi/${extractYoutubeId(data.youtube_id)}/mqdefault.jpg`}
            alt="Video thumbnail"
            className="w-48 h-auto rounded"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
        <textarea
          value={data.description}
          onChange={(e) => setData({ ...data, description: e.target.value })}
          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-red-500"
          rows={2}
          placeholder="Brief description of the video"
          data-testid="video-description-input"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Duration</label>
          <input
            type="text"
            value={data.duration}
            onChange={(e) => setData({ ...data, duration: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-red-500"
            placeholder="e.g., 3:45"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Display Order</label>
          <input
            type="number"
            value={data.order}
            onChange={(e) => setData({ ...data, order: parseInt(e.target.value) || 0 })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-red-500"
          />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id={isEdit ? "video-active-edit" : "video-active-new"}
          checked={data.active}
          onChange={(e) => setData({ ...data, active: e.target.checked })}
          className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
        />
        <label htmlFor={isEdit ? "video-active-edit" : "video-active-new"} className="text-sm font-medium text-slate-700">
          Active (visible on website)
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="video-manager">
      {/* Add Button */}
      {!isAdding && (
        <Button onClick={() => setIsAdding(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="add-video-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Video
        </Button>
      )}

      {/* Add Form */}
      {isAdding && (
        <Card className="border-2 border-red-300">
          <CardHeader className="bg-red-50">
            <CardTitle className="flex items-center gap-2">
              <Youtube className="w-5 h-5 text-red-600" />
              Add New Video
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {renderForm(newVideo, setNewVideo, false)}
            <div className="flex gap-3 pt-4 mt-4 border-t">
              <Button onClick={handleAdd} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-video-btn">
                <Save className="w-4 h-4 mr-2" /> Save Video
              </Button>
              <Button variant="outline" onClick={() => setIsAdding(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Videos */}
      <div className="grid gap-4">
        {videos.length === 0 ? (
          <div className="text-center py-8 text-slate-500">No videos yet. Click "Add Video" to add one.</div>
        ) : (
          videos.map((video) => (
            <Card key={video.id} className={`border-2 ${!video.active ? 'opacity-60' : ''}`} data-testid={`video-card-${video.id}`}>
              <CardContent className="pt-6">
                {editingId === video.id ? (
                  <>
                    {renderForm(editForm, setEditForm, true)}
                    <div className="flex gap-3 pt-4 mt-4 border-t">
                      <Button onClick={handleSaveEdit} className="bg-red-600 hover:bg-red-700 text-white">
                        <Save className="w-4 h-4 mr-2" /> Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => { setEditingId(null); setEditForm({}); }}>Cancel</Button>
                    </div>
                  </>
                ) : (
                  <div className="flex gap-4">
                    {/* Thumbnail */}
                    <div className="relative flex-shrink-0">
                      <img 
                        src={`https://img.youtube.com/vi/${video.youtube_id}/mqdefault.jpg`}
                        alt={video.title}
                        className="w-40 h-24 object-cover rounded-lg"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=200'; }}
                      />
                      <div className="absolute inset-0 bg-black/30 rounded-lg flex items-center justify-center">
                        <Play className="w-8 h-8 text-white" fill="white" />
                      </div>
                      {video.duration && (
                        <Badge className="absolute bottom-1 right-1 bg-black/70 text-white text-xs">
                          {video.duration}
                        </Badge>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg truncate">{video.title}</h3>
                        {!video.active && <Badge variant="secondary">Hidden</Badge>}
                      </div>
                      <p className="text-slate-600 text-sm mb-2 line-clamp-1">{video.description}</p>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-700">{video.category}</Badge>
                        <Badge variant="outline">Order: {video.order}</Badge>
                        <a 
                          href={`https://www.youtube.com/watch?v=${video.youtube_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-red-600 hover:underline"
                        >
                          Watch on YouTube →
                        </a>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button variant="outline" onClick={() => handleEdit(video)} data-testid={`edit-video-${video.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" className="text-red-600" onClick={() => handleDelete(video.id)} data-testid={`delete-video-${video.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
